import { take, uniqBy } from 'lodash';
import { Ellipsis } from 'lucide-react';
import { useSession } from 'next-auth/react';

import { Link } from '@/components/link';
import { DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useSearchHistory } from '@/hooks/use-search-history';
import { ROUTING_SEARCH_URL } from '@/lib/constants';
import { RouteSearchUsageEvent } from '@/types';
import { tv } from '@/utils/styles';

import { SearchHistoryRow } from '../../search-history/row';

const getUniqueEventString = (event: RouteSearchUsageEvent) => {
  if (event.data && 'origin' in event.data && 'destination' in event.data) {
    return `${event.data.origin.placeId}_${event.data.destination.placeId}_${JSON.stringify(event.data.params)}`;
  }
  return event.id;
};

interface AppNavigationSearchHistoryProps {
  isClosed?: boolean;
  limit?: number;
}

const AppNavigationSearchHistory = ({ isClosed, limit = 3 }: AppNavigationSearchHistoryProps) => {
  const { data: session } = useSession();

  const { events } = useSearchHistory({ hasAccess: !!session });
  const filteredEvents = take(
    uniqBy(events, (e) => getUniqueEventString(e)),
    limit,
  );

  const { base, button, itemList, item, itemBg, icon } = styles({ isClosed });

  return filteredEvents?.length > 0 ? (
    <div className={base()}>
      {isClosed && (
        <>
          <DropdownMenuLabel>Recent searches</DropdownMenuLabel>
          <DropdownMenuSeparator />
        </>
      )}
      <div className={itemList()}>
        <ul>
          {filteredEvents.map((event) => {
            return (
              <li key={event.id} className={item()}>
                <SearchHistoryRow key={event.id} event={event} variant="mini" />
                <span className={itemBg()} />
              </li>
            );
          })}
        </ul>
        <Link title="See full search history" className={button()} href={`${ROUTING_SEARCH_URL}/history`}>
          <Ellipsis className={icon()} /> More
        </Link>
      </div>
    </div>
  ) : null;
};

const styles = tv({
  slots: {
    base: 'mt-1.5',
    itemList: 'px-2.5',
    item: 'relative',
    itemBg:
      'item-menu-bg pointer-events-none absolute bottom-0 left-0 right-0 top-0 scale-75 rounded bg-grey-200 opacity-0 transition-all',
    button: 'relative block text-xs text-white opacity-70 hover:opacity-100',
    icon: 'size-4',
  },
  variants: {
    isClosed: {
      true: {
        itemList: 'px-0 pt-1',
        item: 'mb-0.5 px-2 [&_.item-menu-bg]:hover:scale-100 [&_.item-menu-bg]:hover:opacity-100',
        icon: 'ml-2 text-grey-700',
        button: 'text-grey-700',
      },
      false: {
        item: 'opacity-70 hover:opacity-100',
      },
    },
  },
});

export { AppNavigationSearchHistory };
